/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import * as Sentry from "@sentry/react";
import { configuration } from "./configuration";
import { getSessionToken } from "./auth-client";

/**
 * Exchanges the allauth session token for a short-lived JWT.
 * Backed by GET /api/auth/token. Returns null when no session
 * is present; callers should not send the Authorization header in that case.
 *
 * The JWT is cached until shortly before its own expiry, read from the token
 * (see cacheUntil below). Without caching, every protected request would make
 * two round-trips — one for the JWT, one for the payload. The cache is cleared
 * automatically when the session token is absent (that is, after sign-out).
 *
 * Session 383 A-4: this paragraph used to state a 15-minute token and a fixed
 * 13-minute cache. Both numbers are gone. The lifetime is configuration on the
 * server (JWT_ACCESS_TOKEN_LIFETIME_MINUTES, 60 by default, 15 for consumers
 * other than the shop) and the cache follows the token rather than guessing at
 * it — a fixed cache can outlive the token it holds, and then every protected
 * request fails until the cache clears.
 *
 * B4/Q1: ``audience`` stamps a per-consumer aud claim into the token so a
 * JWT minted for the cart cannot be replayed against the forum or the chatbot.
 * When omitted the backend default is used.
 */
/**
 * F-2 / N-5: ONE ENTRY PER AUDIENCE, keyed by audience.
 *
 * This used to be four module variables holding one token, one expiry, one
 * session and one audience. Asking for a token for a second audience
 * overwrote the first. Nothing went wrong while the shopping cart was the only
 * consumer that ever passed one — but the moment a second consumer starts
 * asking, the two evict each other on every switch and each pays an extra
 * round trip. A Map keyed by audience is the right shape, and it is the ONLY
 * cache: keeping the old variables alongside it would be two caches describing
 * the same thing, and they would disagree the first time somebody edited one
 * path and not the other.
 *
 * N2: each entry records the session token it was minted for. On a shared
 * browser, if one person signs out and another signs in inside the cache
 * window, a token minted for the first must never be served to the second.
 */
const jwtCache = new Map<
  string,
  { token: string; expiry: number; session: string }
>();

/**
 * The margin between a cached token's expiry and the token's own expiry.
 *
 * A token handed out at the very end of its cached life must still have time to
 * be used, and the browser's clock and the server's clock are not the same
 * clock. Do NOT reduce this to zero — that reintroduces the race where a cached
 * token expires between being handed out and being presented.
 */
const EXPIRY_MARGIN_MS = 2 * 60 * 1000;

/**
 * How long a token may be cached, read from the token itself.
 *
 * Session 383 A-4. This used to be a fixed thirteen minutes, chosen against a
 * hardcoded fifteen-minute token. V-1 makes the lifetime configurable and
 * lengthens the default to sixty minutes, and anybody who sets it BELOW
 * thirteen would have given every signed-in visitor a cached token that had
 * already expired — every protected request failing until the cache cleared,
 * on every page, for everybody. The bound settings.py puts on
 * JWT_ACCESS_TOKEN_LIFETIME_MINUTES reduces that risk; it does not remove it,
 * and a cache that follows the token needs no bound at all.
 *
 * The `exp` claim is read without verifying the signature, which is correct
 * here: this is our own cache deciding when to re-mint, not an authorisation
 * decision. A forged `exp` can only make this browser re-mint sooner or hold a
 * token the server will then refuse.
 */
function cacheUntil(jwt: string, now: number): number {
  try {
    const claims = JSON.parse(atob(jwt.split(".")[1])) as { exp?: number };
    if (typeof claims.exp !== "number") throw new Error("no exp");
    // Never below thirty seconds: a token that is already near expiry must not
    // turn every request into a fresh mint.
    return Math.max(now + 30_000, claims.exp * 1000 - EXPIRY_MARGIN_MS);
  } catch {
    // Unreadable token: cache briefly rather than not at all, so a malformed
    // token cannot turn into a request storm.
    return now + 60_000;
  }
}

/** How long to wait for the mint before giving up on it. */
const JWT_FETCH_TIMEOUT_MS = 5_000;

/** Resets the JWT cache for every audience. Call after sign-out or in tests. */
export function clearJwtCache(): void {
  jwtCache.clear();
}

export const getJsonWebToken = async (
  audience?: string,
): Promise<string | null> => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    // Session gone (signed out) — discard every cached token. This is the one
    // condition that justifies emptying the whole cache: no audience has a
    // valid token once there is no session behind them.
    clearJwtCache();
    return null;
  }

  const key = audience ?? "";
  const now = Date.now();
  const cached = jwtCache.get(key);
  if (cached && now < cached.expiry && cached.session === sessionToken) {
    return cached.token;
  }

  const params = audience ? `?audience=${encodeURIComponent(audience)}` : "";
  try {
    const res = await fetch(
      `${configuration.backendURL}/api/auth/token${params}`,
      {
        // AllauthSessionTokenAuthentication reads X-Session-Token — not Bearer.
        headers: { "X-Session-Token": sessionToken },
        // F-2: the platform's own timeout rather than a hand-rolled one. It
        // needs no timer to clear, so it cannot leak a pending timer when the
        // request throws, and it rejects with a distinguishable TimeoutError —
        // which is what lets a service that has started timing out for
        // everybody be reported separately from an ordinary network failure.
        signal: AbortSignal.timeout(JWT_FETCH_TIMEOUT_MS),
      },
    );

    if (!res.ok) {
      // F-2: delete ONLY the refused audience.
      //
      // Emptying the whole cache here is what the obvious implementation does
      // and it is wrong. A chat origin missing from the backend allow-list is
      // exactly the condition being guarded against, and it would mean every
      // attempt to open the chat also threw away the shopping cart's perfectly
      // good token — so the customer pays for a re-mint on their way to
      // checkout, because of a fault in an unrelated feature.
      jwtCache.delete(key);
      // F-2: reported HERE and nowhere else. Reporting the same refusal from
      // the calling component as well would split one incident across two
      // names and make its volume look twice its real size. This helper sees
      // every consumer, so one report here covers all of them, and the
      // audience is a tag so any one consumer can still be filtered out.
      Sentry.captureMessage("jwt_mint_refused", {
        level: "warning",
        tags: { audience: audience ?? "default" },
        extra: { status: res.status },
      });
      return null;
    }

    const data = (await res.json()) as { token?: string };
    const token = data.token ?? null;
    if (!token) {
      jwtCache.delete(key);
      return null;
    }

    jwtCache.set(key, {
      token,
      expiry: cacheUntil(token, now),
      session: sessionToken,
    });
    return token;
  } catch (error) {
    jwtCache.delete(key);
    Sentry.captureMessage(
      error instanceof DOMException && error.name === "TimeoutError"
        ? "jwt_mint_timeout"
        : "jwt_mint_network_error",
      { level: "warning", tags: { audience: audience ?? "default" } },
    );
    return null;
  }
};

/**
 * Appends the short-lived JWT to a redirect URL as the `authToken` query param,
 * the agreed hand-off contract with the shopping cart. Returns the URL unchanged
 * when no JWT is available, so the redirect never silently fails.
 *
 * Single source of truth for the param name and encoding — every screen that
 * hands the user back to the cart (login, password reset, OAuth callback) uses
 * this so the contract can never drift between them.
 */
export function appendAuthToken(url: string, jwt: string | null): string {
  if (!jwt) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}authToken=${encodeURIComponent(jwt)}`;
}

/**
 * Mints a JWT and redirects the browser to `url` with the token appended as
 * `authToken`. When a session is present but the token call fails (e.g. an
 * audience mismatch between the frontend shop URL and the backend
 * ALLOWED_JWT_AUDIENCES), this function returns `{ ok: false }` so callers
 * can surface a visible error instead of forwarding an unauthenticated user.
 *
 * F-6 guard: a null JWT with a live session means the backend rejected the
 * audience. Forwarding silently in that case would land the user on the shop
 * without auth — this makes the failure visible.
 *
 * When no session is present (logged-out), the redirect proceeds without a
 * token so the shop can present its own login flow.
 *
 * B4/Q1: Derives the audience from the origin of `url` so each consumer
 * receives a JWT whose aud claim matches only that consumer's identifier,
 * preventing cross-consumer token replay.
 */
export async function redirectWithAuthToken(
  url: string,
  { sessionExpected = false }: { sessionExpected?: boolean } = {},
): Promise<
  { ok: true } | { ok: false; reason: "token_failed"; cause?: "no_session" }
> {
  // Derive the consumer audience from the redirect URL's origin.
  // For example https://shop.deskshare.com/... → audience "https://shop.deskshare.com".
  // Falls back to undefined (backend default) when origin cannot be parsed.
  let audience: string | undefined;
  try {
    /* c8 ignore next -- new URL().origin is empty only for blob:/data: URLs, never used here */
    audience = new URL(url).origin || undefined;
  } catch {
    audience = undefined;
  }

  const hasSession = !!getSessionToken();

  // N-3 (Session 447): called straight after a successful sign-in, so a
  // missing session key is not a signed-out visitor - it is a sign-in whose
  // key was not stored or is not yet readable. The guard below covers
  // "session present, token refused" and reports it; it never covered
  // "session absent", and appendAuthToken returns the address unchanged when
  // the token is null. So the customer was forwarded to the cart with no
  // token, { ok: true } was returned, and nothing recorded that an
  // authenticated hand-off had silently become an anonymous one. From the
  // customer's side that is: arrive at the cart signed out, press Log in, and
  // be back on an empty log-in form having watched a loading state come and
  // go - which is what the Session 447 bug report describes.
  //
  // The Sentry report is the part worth insisting on. Whether this happens at
  // all is currently unmeasurable, and that is exactly the question the report
  // turns on.
  if (sessionExpected && !hasSession) {
    Sentry.captureMessage("auth_handoff_no_session", {
      level: "warning",
      tags: { audience: audience ?? "default" },
    });
    return { ok: false, reason: "token_failed", cause: "no_session" };
  }

  const jwt = await getJsonWebToken(audience);

  // F-6 / S11: If we have a live session but no token, the audience was
  // rejected by the backend (ALLOWED_JWT_AUDIENCES mismatch). This is a
  // configuration fault, not a user error, and forwarding silently would land
  // the customer on the shop without auth — so the caller is told and can show
  // a visible failure.
  //
  // F-2 (Session 369): the Sentry report that used to be here has gone. It
  // named the same refusal that getJsonWebToken already reports as
  // "jwt_mint_refused", so one refused mint produced two entries under two
  // names — which splits a real incident across two issues and makes its
  // volume look twice its size. The mint helper sees every consumer and tags
  // the audience, so nothing is lost by reporting there only.
  if (hasSession && !jwt) {
    return { ok: false, reason: "token_failed" };
  }

  window.location.href = appendAuthToken(url, jwt);
  return { ok: true };
}
